import { useState } from 'react';
import { User, BookOpen, HelpCircle, CreditCard, LogOut, Users, TrendingUp, PlusCircle, ChevronRight, ArrowLeft } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';

import { getLocalizedPath } from '@/utils/languageUtils';
import { openExternal } from '@/utils/openExternal';

interface MobileMoreDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface SubMenuItem {
  labelKey: string;
  path: string;
}

interface MenuItem {
  icon: React.ComponentType<any>;
  labelKey: string;
  action?: () => void;
  subItems?: SubMenuItem[];
}

const MobileMoreDrawer = ({ open, onOpenChange }: MobileMoreDrawerProps) => {
  const navigate = useLocalizedNavigate();
  const { signOut } = useAuth();
  const { t, language } = useLanguage();
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);

  const handleNav = (path: string) => {
    onOpenChange(false);
    setActiveSubmenu(null);
    navigate(path);
  };

  const handleExternal = (path: string) => {
    onOpenChange(false);
    setActiveSubmenu(null);
    const fullUrl = `${window.location.origin}${getLocalizedPath(path, language)}`;
    openExternal(fullUrl);
  };

  const handleSignOut = async () => {
    onOpenChange(false);
    setActiveSubmenu(null);
    await signOut();
    navigate('/sign-in');
  };

  const menuItems: MenuItem[] = [
    {
      icon: User,
      labelKey: 'backoffice.sidebarUser.sections.myProfile.title',
      subItems: [
        { labelKey: 'backoffice.sidebarUser.sections.myProfile.items.theClubUserProfile', path: '/profile/the-club-user-profile' },
        { labelKey: 'backoffice.sidebarUser.sections.myProfile.items.personalInformation', path: '/profile/personal-info' },
        { labelKey: 'backoffice.sidebarUser.sections.myProfile.items.paymentMethods', path: '/profile/payment-methods' },
      ],
    },
    {
      icon: PlusCircle,
      labelKey: 'backoffice.sidebarUser.sections.investmentAccounts.items.addAccount',
      action: () => handleNav('/add-account'),
    },
    {
      icon: Users,
      labelKey: 'backoffice.sidebarUser.sections.myCommunity.title',
      subItems: [
        { labelKey: 'backoffice.sidebarUser.sections.myCommunity.items.genealogyTree', path: '/my-community/genealogy-tree' },
        { labelKey: 'backoffice.sidebarUser.sections.myCommunity.items.communityMembers', path: '/my-community/members' },
      ],
    },
    {
      icon: TrendingUp,
      labelKey: 'backoffice.mobile.more.myProfitHistory',
      subItems: [
        { labelKey: 'backoffice.sidebarUser.sections.myProfits.items.accountsProfitHistory', path: '/my-profits/accounts-history' },
        { labelKey: 'backoffice.sidebarUser.sections.myProfits.items.commissionsHistory', path: '/my-profits/commissions-history' },
        { labelKey: 'backoffice.sidebarUser.sections.myProfits.items.commissionsPaymentHistory', path: '/my-profits/commissions-payment-history' },
      ],
    },
    {
      icon: BookOpen,
      labelKey: 'backoffice.sidebarUser.sections.tutorials.title',
      action: () => handleExternal('/tutorials'),
    },
    {
      icon: HelpCircle,
      labelKey: 'backoffice.sidebarUser.sections.support.title',
      action: () => handleExternal('/contact-us'),
    },
  ];

  const activeMenu = activeSubmenu
    ? menuItems.find((item) => item.labelKey === activeSubmenu)
    : null;

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) setActiveSubmenu(null);
    onOpenChange(isOpen);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl"
        style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <SheetHeader>
          <SheetTitle className="text-left">
            {activeMenu ? (
              <button
                onClick={() => setActiveSubmenu(null)}
                className="flex items-center gap-2 text-foreground"
              >
                <ArrowLeft className="h-5 w-5" />
                <span>{t(activeMenu.labelKey)}</span>
              </button>
            ) : (
              t('backoffice.mobile.tabs.more')
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-1">
          {activeMenu && activeMenu.subItems ? (
            /* Sub-items view */
            activeMenu.subItems.map((sub, i) => (
              <button
                key={i}
                onClick={() => handleNav(sub.path)}
                className="flex items-center gap-3 w-full px-3 py-3 rounded-lg text-foreground hover:bg-accent transition-colors text-left"
              >
                <span className="text-sm font-medium">{t(sub.labelKey)}</span>
              </button>
            ))
          ) : (
            /* Main menu view */
            <>
              {menuItems.map((item, i) => {
                const Icon = item.icon;
                const hasSubItems = !!item.subItems;
                return (
                  <button
                    key={i}
                    onClick={() => {
                      if (hasSubItems) {
                        setActiveSubmenu(item.labelKey);
                      } else if (item.action) {
                        item.action();
                      }
                    }}
                    className="flex items-center gap-3 w-full px-3 py-3 rounded-lg text-foreground hover:bg-accent transition-colors text-left"
                  >
                    <Icon className="h-5 w-5 text-muted-foreground" />
                    <span className="text-sm font-medium flex-1">{t(item.labelKey)}</span>
                    {hasSubItems && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                  </button>
                );
              })}
              {/* Sign Out */}
              <button
                onClick={handleSignOut}
                className="flex items-center gap-3 w-full px-3 py-3 rounded-lg text-destructive hover:bg-destructive/10 transition-colors text-left mt-2"
              >
                <LogOut className="h-5 w-5" />
                <span className="text-sm font-medium">{t('backoffice.sidebarUser.signOut')}</span>
              </button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileMoreDrawer;
